'use client';

import React from 'react';
import styled, { useTheme } from 'styled-components';

export type ZoneVariant = 'service' | 'restricted' | 'depot' | 'travel';

export interface MapZoneProps {
  /** Zone semantic. Drives fill, stroke colour, and border style. */
  variant: ZoneVariant;
  /** Optional human-readable label rendered inside the swatch. */
  label?: string;
  /** Width as px number or any CSS length. Default 160. */
  width?: number | string;
  /** Height as px number or any CSS length. Default 90. */
  height?: number | string;
  'aria-label'?: string;
  className?: string;
}

interface VariantTokens {
  fill: string;
  stroke: string;
  dashed: boolean;
}

function variantTokens(theme: any, variant: ZoneVariant): VariantTokens {
  switch (variant) {
    case 'service':
      return {
        fill: theme.colors.activity.pickup.fill,
        stroke: theme.colors.activity.pickup.bar,
        dashed: false,
      };
    case 'restricted':
      return {
        fill: theme.colors.status.noShow.bg,
        stroke: theme.colors.status.noShow.dot,
        dashed: true,
      };
    case 'depot':
      return {
        fill: theme.colors.activity.depot.fill,
        stroke: theme.colors.activity.depot.bar,
        dashed: false,
      };
    case 'travel':
      return {
        fill: theme.colors.activity.deadhead.fill,
        stroke: theme.colors.activity.deadhead.bar,
        dashed: false,
      };
  }
}

const Root = styled.div<{ $fill: string; $stroke: string; $dashed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(p) => p.$fill};
  border: 2px ${(p) => (p.$dashed ? 'dashed' : 'solid')} ${(p) => p.$stroke};
  box-sizing: border-box;
`;

const Label = styled.div<{ $color: string }>`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => p.$color};
  text-transform: none;
`;

/**
 * Visual specimen for a geofenced map zone. Shows the canonical fill,
 * stroke colour and border style for a zone semantic. For real map
 * polygons, use the same theme tokens with your map library.
 *
 * @example
 * <MapZone variant="service" label="CBD service area" />
 */
export function MapZone({
  variant,
  label,
  width = 160,
  height = 90,
  'aria-label': ariaLabel,
  className,
}: MapZoneProps) {
  const theme = useTheme() as any;
  const { fill, stroke, dashed } = variantTokens(theme, variant);
  const computedAria = ariaLabel ?? (label ? `${variant} zone: ${label}` : `${variant} zone`);

  return (
    <Root
      role="img"
      aria-label={computedAria}
      data-variant={variant}
      data-style={dashed ? 'dashed' : 'solid'}
      className={className}
      style={{ width, height }}
      $fill={fill}
      $stroke={stroke}
      $dashed={dashed}
    >
      {label && <Label $color={stroke}>{label}</Label>}
    </Root>
  );
}
